import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';
import firebase from 'firebase/compat/app';

import AccountPickerContext from 'contexts/AccountPickerContext';
import MetricCacheContext from 'contexts/MetricCacheContext';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';
import captureException from 'services/captureException';
import STORE from 'store';

import getIsIdResolved, { SPECIAL_METRIC_IDS } from './getIsIdResolved';

const BATCH_SIZE = 30;

interface FetchArgs {
  idsToFetch: string[];
  accountId: string;
}

interface FetchResponse {
  normalResults: Metrics.NormalMetric[];
  compoundResults: Metrics.CompoundMetric[];
  inputMetricIds: string[];
  queriedIds: string[];
  erroredIds: string[];
}

interface RequestAllArgs {
  allNormalRequested: boolean;
  allCompoundRequested: boolean;
  accountId: string;
}

interface RequestAllResponse {
  normalResults: Metrics.NormalMetric[];
  compoundResults: Metrics.CompoundMetric[];
}

const MetricCacheProvider = ({ children }: { children: ReactNode }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);

  const [normalMetricsCache, setNormalMetricsCache] = useState<
    Map<string, Metrics.NormalMetric>
  >(new Map());
  const [compoundMetricsCache, setCompoundMetricsCache] = useState<
    Map<string, Metrics.CompoundMetric>
  >(new Map());

  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [erroredIds, setErroredIds] = useState<Set<string>>(new Set());

  const [allNormalRequested, setAllNormalRequested] = useState(false);
  const allNormalRequestedRef = useRef(allNormalRequested);
  allNormalRequestedRef.current = allNormalRequested;
  const [allCompoundRequested, setAllCompoundRequested] = useState(false);
  const allCompoundRequestedRef = useRef(allCompoundRequested);
  allCompoundRequestedRef.current = allCompoundRequested;
  const [allMetricsLoaded, setAllMetricsLoaded] = useState(false);

  const normalCacheRef = useRef(normalMetricsCache);
  normalCacheRef.current = normalMetricsCache;
  const compoundCacheRef = useRef(compoundMetricsCache);
  compoundCacheRef.current = compoundMetricsCache;

  // Listen to cache version for invalidation.
  // The first onSnapshot fires with the current doc state — we skip it since
  // we only care about subsequent writes that indicate a metric was changed.
  // The ref is reset when selectedAccountId changes so the new listener also
  // skips its initial snapshot. This is safe because onSnapshot always fires
  // asynchronously (even with cached data), so the synchronous reset on
  // line below completes before any callback.
  const isFirstSnapshot = useRef(true);
  useEffect(() => {
    isFirstSnapshot.current = true;
    const unsubscribe = STORE.metricDefinitions
      .getMetricCacheVersionRef({ accountId: selectedAccountId })
      .onSnapshot((snapshot) => {
        if (isFirstSnapshot.current) {
          isFirstSnapshot.current = false;
          return;
        }

        if (!snapshot.exists) {
          return;
        }

        const data = snapshot.data() as
          | {
              normalMetricIds?: string[];
              compoundMetricIds?: string[];
            }
          | undefined;

        const normalIds = data?.normalMetricIds;
        const compoundIds = data?.compoundMetricIds;

        if (!normalIds && !compoundIds) {
          // Legacy write without IDs — nuclear clear
          setNormalMetricsCache(new Map());
          setCompoundMetricsCache(new Map());
          setPendingIds([]);
          setErroredIds(new Set());
          setAllMetricsLoaded(false);
          return;
        }

        // Surgical re-fetch: only re-fetch IDs that are already cached
        // (stale data) or where all metrics were requested (e.g. DataManager).
        // This avoids unnecessary Firestore reads and re-render cascades for
        // users who never requested the invalidated metrics.
        const accountId = selectedAccountId;
        const currentNormalCache = normalCacheRef.current;
        const currentCompoundCache = compoundCacheRef.current;
        const refetchNormalIds = (normalIds || []).filter(
          (id) => currentNormalCache.has(id) || allNormalRequestedRef.current,
        );
        const refetchCompoundIds = (compoundIds || []).filter(
          (id) =>
            currentCompoundCache.has(id) || allCompoundRequestedRef.current,
        );

        if (refetchNormalIds.length === 0 && refetchCompoundIds.length === 0) {
          return;
        }

        const docId = firebase.firestore.FieldPath.documentId();

        const refetchPromises: Promise<void>[] = [];

        if (refetchNormalIds.length > 0) {
          const batches = _.chunk(refetchNormalIds, BATCH_SIZE);
          for (const batch of batches) {
            refetchPromises.push(
              STORE.metricDefinitions
                .getNormalMetricsRef({ accountId })
                .where(docId, 'in', batch)
                .get()
                .then((snap) => {
                  const fetched = new Map<string, Metrics.NormalMetric>();
                  snap.docs.forEach((doc) => {
                    const d = doc.data();
                    if (!d.isHidden) {
                      fetched.set(d.id, d);
                    }
                  });
                  setNormalMetricsCache((prev) => {
                    const next = new Map(prev);
                    batch.forEach((id) => {
                      const fresh = fetched.get(id);
                      if (fresh) {
                        next.set(id, fresh);
                      } else {
                        next.delete(id);
                      }
                    });
                    return next;
                  });
                })
                .catch((e: unknown) => {
                  const error = e instanceof Error ? e : new Error(String(e));
                  error.name =
                    'MetricCacheProvider - surgical refetch normal failed';
                  captureException(error);
                }),
            );
          }
        }

        if (refetchCompoundIds.length > 0) {
          const batches = _.chunk(refetchCompoundIds, BATCH_SIZE);
          for (const batch of batches) {
            refetchPromises.push(
              STORE.metricDefinitions
                .getCompoundMetricsRef({ accountId })
                .where(docId, 'in', batch)
                .get()
                .then((snap) => {
                  const fetched = new Map<string, Metrics.CompoundMetric>();
                  snap.docs.forEach((doc) => {
                    const d = doc.data();
                    if (!d.isHidden) {
                      fetched.set(d.id, d);
                    }
                  });
                  setCompoundMetricsCache((prev) => {
                    const next = new Map(prev);
                    batch.forEach((id) => {
                      const fresh = fetched.get(id);
                      if (fresh) {
                        next.set(id, fresh);
                      } else {
                        next.delete(id);
                      }
                    });
                    return next;
                  });
                })
                .catch((e: unknown) => {
                  const error = e instanceof Error ? e : new Error(String(e));
                  error.name =
                    'MetricCacheProvider - surgical refetch compound failed';
                  captureException(error);
                }),
            );
          }
        }

        // Individual promises handle errors via captureException.
        // Outer catch prevents unhandled rejection if a catch re-throws.
        Promise.all(refetchPromises).catch(() => {});
      });

    return () => {
      unsubscribe();
    };
  }, [selectedAccountId]);

  // Reset on account change
  useEffect(() => {
    return () => {
      setPendingIds([]);
      setNormalMetricsCache(new Map());
      setCompoundMetricsCache(new Map());
      setErroredIds(new Set());
      setAllNormalRequested(false);
      setAllCompoundRequested(false);
      setAllMetricsLoaded(false);
    };
  }, [selectedAccountId]);

  // --- On-demand fetch ---

  const fetchArgs = useMemo<FetchArgs | undefined>(() => {
    // Expand compound inputs: if a requested ID is already cached as a
    // compound metric, also include its input metric IDs.
    const expandedIds: string[] = [...pendingIds];
    pendingIds.forEach((id) => {
      const cached = compoundMetricsCache.get(id);
      if (cached) {
        expandedIds.push(...cached.metricIds);
      }
    });

    const idsToFetch = _.uniq(expandedIds).filter(
      (id) =>
        !normalMetricsCache.has(id) &&
        !compoundMetricsCache.has(id) &&
        !erroredIds.has(id) &&
        !SPECIAL_METRIC_IDS.has(id),
    );

    if (idsToFetch.length === 0) return undefined;
    return { idsToFetch, accountId: selectedAccountId };
  }, [
    pendingIds,
    normalMetricsCache,
    compoundMetricsCache,
    erroredIds,
    selectedAccountId,
  ]);

  const fetchCallback = useCallback(
    async (args: FetchArgs): Promise<FetchResponse> => {
      const normalResults: Metrics.NormalMetric[] = [];
      const compoundResults: Metrics.CompoundMetric[] = [];
      const inputMetricIds: string[] = [];
      const erroredBatchIds: string[] = [];

      const docId = firebase.firestore.FieldPath.documentId();
      const batches = _.chunk(args.idsToFetch, BATCH_SIZE);

      const fetchBatch = async (batch: string[]) => {
        try {
          const normalRef = STORE.metricDefinitions.getNormalMetricsRef({
            accountId: args.accountId,
          });
          const normalSnapshot = await normalRef
            .where(docId, 'in', batch)
            .get();

          const foundNormalIds = new Set<string>();
          normalSnapshot.docs.forEach((doc) => {
            const data = doc.data();
            const id = data.id || doc.id;
            if (!data.isHidden) {
              foundNormalIds.add(id);
              normalResults.push(data);
            }
          });

          const missingIds = batch.filter((id) => !foundNormalIds.has(id));
          if (missingIds.length > 0) {
            const compoundRef = STORE.metricDefinitions.getCompoundMetricsRef({
              accountId: args.accountId,
            });
            const compoundSnapshot = await compoundRef
              .where(docId, 'in', missingIds)
              .get();

            const foundCompoundIds = new Set<string>();
            compoundSnapshot.docs.forEach((doc) => {
              const data = doc.data();
              if (!data.isHidden) {
                foundCompoundIds.add(data.id || doc.id);
                compoundResults.push(data);
                inputMetricIds.push(...data.metricIds);
              }
            });

            const notFoundIds = missingIds.filter(
              (id) => !foundCompoundIds.has(id),
            );
            erroredBatchIds.push(...notFoundIds);
          }
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          error.name = 'MetricCacheProvider - batch fetch failed';
          captureException(error);
          erroredBatchIds.push(...batch);
        }
      };

      await Promise.all(batches.map(fetchBatch));

      return {
        normalResults,
        compoundResults,
        inputMetricIds,
        queriedIds: args.idsToFetch,
        erroredIds: erroredBatchIds,
      };
    },
    [],
  );

  const fetchResponseHandler = useCallback((response: FetchResponse) => {
    if (response.normalResults.length > 0) {
      setNormalMetricsCache((prev) => {
        const next = new Map(prev);
        response.normalResults.forEach((m) => next.set(m.id, m));
        return next;
      });
    }

    if (response.compoundResults.length > 0) {
      setCompoundMetricsCache((prev) => {
        const next = new Map(prev);
        response.compoundResults.forEach((m) => next.set(m.id, m));
        return next;
      });
    }

    if (response.erroredIds.length > 0) {
      setErroredIds((prev) => {
        const next = new Set(prev);
        response.erroredIds.forEach((id) => next.add(id));
        return next;
      });
    }

    const queriedSet = new Set(response.queriedIds);
    setPendingIds((prev) => {
      const filtered = prev.filter((id) => !queriedSet.has(id));
      const withInputs = [...filtered, ...response.inputMetricIds];
      return withInputs.length === prev.length &&
        withInputs.every((id, i) => id === prev[i])
        ? prev
        : withInputs;
    });
  }, []);

  useLockedDebouncedEffect({
    args: fetchArgs,
    callback: fetchCallback,
    responseHandler: fetchResponseHandler,
  });

  // --- Request all ---

  const requestAllArgs = useMemo<RequestAllArgs | undefined>(() => {
    if (!allNormalRequested && !allCompoundRequested) return undefined;
    return {
      allNormalRequested,
      allCompoundRequested,
      accountId: selectedAccountId,
    };
  }, [allNormalRequested, allCompoundRequested, selectedAccountId]);

  const requestAllCallback = useCallback(
    async (args: RequestAllArgs): Promise<RequestAllResponse> => {
      const normalResults: Metrics.NormalMetric[] = [];
      const compoundResults: Metrics.CompoundMetric[] = [];
      const queries: Promise<void>[] = [];

      if (args.allNormalRequested) {
        queries.push(
          STORE.metricDefinitions
            .getNormalMetricsRef({ accountId: args.accountId })
            .where('status', 'in', ['core', 'public', 'archived'])
            .get()
            .then((snapshot) => {
              snapshot.docs.forEach((doc) => {
                const data = doc.data();
                if (!data.isHidden) {
                  normalResults.push(data);
                }
              });
            })
            .catch((e: unknown) => {
              const error = e instanceof Error ? e : new Error(String(e));
              error.name =
                'MetricCacheProvider - requestAll normal metrics failed';
              captureException(error);
            }),
        );
      }

      if (args.allCompoundRequested) {
        queries.push(
          STORE.metricDefinitions
            .getCompoundMetricsRef({ accountId: args.accountId })
            .get()
            .then((snapshot) => {
              snapshot.docs.forEach((doc) => {
                const data = doc.data();
                if (!data.isHidden) {
                  compoundResults.push(data);
                }
              });
            })
            .catch((e: unknown) => {
              const error = e instanceof Error ? e : new Error(String(e));
              error.name =
                'MetricCacheProvider - requestAll compound metrics failed';
              captureException(error);
            }),
        );
      }

      await Promise.all(queries);
      return { normalResults, compoundResults };
    },
    [],
  );

  const requestAllResponseHandler = useCallback(
    (response: RequestAllResponse) => {
      if (response.normalResults.length > 0) {
        setNormalMetricsCache((prev) => {
          const next = new Map(prev);
          response.normalResults.forEach((m) => next.set(m.id, m));
          return next;
        });
      }

      if (response.compoundResults.length > 0) {
        setCompoundMetricsCache((prev) => {
          const next = new Map(prev);
          response.compoundResults.forEach((m) => next.set(m.id, m));
          return next;
        });
      }

      setAllMetricsLoaded(true);
    },
    [],
  );

  useLockedDebouncedEffect({
    args: requestAllArgs,
    callback: requestAllCallback,
    responseHandler: requestAllResponseHandler,
  });

  // --- Request functions ---

  const requestMetricsByIds = useCallback((ids: string[]) => {
    setPendingIds((prev) => {
      const next = _.uniq([...prev, ...ids]);
      return _.isEqual(next, prev) ? prev : next;
    });
  }, []);

  const requestAllNormalMetrics = useCallback(() => {
    setAllNormalRequested(true);
  }, []);

  const requestAllCompoundMetrics = useCallback(() => {
    setAllCompoundRequested(true);
  }, []);

  // --- Resolution checks ---

  const isIdResolved = useCallback(
    (id: string) =>
      getIsIdResolved({
        id,
        normalMetricsCache,
        compoundMetricsCache,
        erroredIds,
      }),
    [normalMetricsCache, compoundMetricsCache, erroredIds],
  );

  const isIdErrored = useCallback(
    (id: string) => {
      return erroredIds.has(id);
    },
    [erroredIds],
  );

  const value = useMemo(
    () => ({
      normalMetricsCache,
      compoundMetricsCache,
      requestMetricsByIds,
      requestAllNormalMetrics,
      requestAllCompoundMetrics,
      allMetricsLoaded,
      isIdResolved,
      isIdErrored,
    }),
    [
      normalMetricsCache,
      compoundMetricsCache,
      requestMetricsByIds,
      requestAllNormalMetrics,
      requestAllCompoundMetrics,
      allMetricsLoaded,
      isIdResolved,
      isIdErrored,
    ],
  );

  return (
    <MetricCacheContext.Provider value={value}>
      {children}
    </MetricCacheContext.Provider>
  );
};

export default MetricCacheProvider;
